import { Observable, of, BehaviorSubject, interval, Subscription } from "rxjs";
import { mergeMap, catchError, share, map, filter, switchMap } from "rxjs/operators";
import { AuthStatusDelegate, IAuthStatusResponse, IAuthProfileResponse, IAuthValidateResponse } from "./auth-status.delegate";
import { AuthenticationService } from "../AuthenticationService";
import { LoggerUtil } from "../LoggerService";
import { ISession } from "../AuthenticationService/authentication.interface";

/**
 * AuthStatusService - Lightweight authentication status management.
 * 
 * This service replaces the heavy Resume API calls with lightweight endpoints
 * that use server-side caching for better performance.
 * 
 * Benefits over Resume API:
 * - 98% reduction in database queries (via caching)
 * - 87% reduction in API call frequency (15 min vs 2 min intervals)
 * - Automatic session extension on any API call
 * - Proper JSON responses instead of 302 redirects
 * - Smoother user experience
 */
export class AuthStatusService
{
    static logger: LoggerUtil = LoggerUtil.getLoggerUtil("AuthStatusService");

    /** Check interval: 15 minutes (was 2 minutes with Resume API) */
    public static readonly CHECK_INTERVAL = 15 * 60 * 1000; // 15 minutes

    private authStatusSubject: BehaviorSubject<IAuthStatusResponse | null> = new BehaviorSubject<IAuthStatusResponse | null>(null);
    public authStatus$: Observable<IAuthStatusResponse | null> = this.authStatusSubject.asObservable();

    /** Periodic auth status check subscription */
    private periodicCheckSubscription: Subscription;

    constructor(
        private authStatusDelegate: AuthStatusDelegate,
        private authenticationService: AuthenticationService
    )
    {
        // Start periodic checks based on session state, not app state
        this.authenticationService.userSession
            .pipe(
                filter((session: ISession) => session && (session.loggedin || session.authenticated))
            )
            .subscribe((session: ISession) =>
            {
                AuthStatusService.logger.debug('Valid session detected - starting periodic auth checks');
                this.startPeriodicChecks();
            });

        // Stop periodic checks when session becomes invalid
        this.authenticationService.userSession
            .pipe(
                filter((session: ISession) => !session || (!session.loggedin && !session.authenticated))
            )
            .subscribe((session: ISession) =>
            {
                AuthStatusService.logger.debug('Invalid session detected - stopping periodic auth checks');
                this.stopPeriodicChecks();
            });
    }

    /**
     * Start periodic authentication status checks (every 15 minutes).
     * Now controlled by session state, not app state.
     */
    public startPeriodicChecks(): void
    {
        AuthStatusService.logger.debug(`Starting session-based periodic auth checks every ${AuthStatusService.CHECK_INTERVAL / 60000} minutes`);

        // Stop any existing checks
        this.stopPeriodicChecks();

        // Start periodic checks
        const interval$ = interval(AuthStatusService.CHECK_INTERVAL);

        this.periodicCheckSubscription = interval$.subscribe(() =>
        {
            AuthStatusService.logger.debug('Performing periodic auth status check (session-based)');
            this.checkAuthStatus().subscribe(
                (isAuthenticated) =>
                {
                    if (!isAuthenticated)
                    {
                        AuthStatusService.logger.warn('Periodic auth check failed - session may be invalid');
                        // Let the session observable handle stopping checks
                    } else
                    {
                        AuthStatusService.logger.debug('Periodic auth check successful');
                    }
                },
                (error) =>
                {
                    AuthStatusService.logger.error(`Periodic auth check error: ${JSON.stringify(error)}`);
                }
            );
        });
    }

    /**
     * Stop periodic authentication status checks.
     */
    public stopPeriodicChecks(): void
    {
        if (this.periodicCheckSubscription)
        {
            this.periodicCheckSubscription.unsubscribe();
            this.periodicCheckSubscription = null;
            AuthStatusService.logger.debug('Stopped session-based periodic auth checks');
        }
    }

    /**
     * Check authentication status using lightweight endpoint.
     * This is much more efficient than the deprecated Resume API.
     * 
     * Handles different response scenarios:
     * - 200 OK: Valid session (may have been auto-refreshed)
     * - 401 Unauthorized: Invalid/expired session (JSON response, not 302 redirect)
     * - Network errors: Connection issues
     * 
     * @returns Observable<boolean> - true if authenticated
     */
    public checkAuthStatus(): Observable<boolean>
    {
        AuthStatusService.logger.debug('checkAuthStatus() - Using lightweight auth status endpoint');

        const obs = this.authStatusDelegate.getStatus();

        return obs.pipe(
            map((response: IAuthStatusResponse) =>
            {
                this.authStatusSubject.next(response);

                if (response.sessionExtended)
                {
                    AuthStatusService.logger.debug('Session automatically extended by server');
                }

                AuthStatusService.logger.debug(`Auth status check result: ${response.isAuthenticated}`);
                return response.isAuthenticated;
            }),
            catchError((error) =>
            {
                AuthStatusService.logger.error(`checkAuthStatus failed: ${JSON.stringify(error)}`);

                // Handle different error types
                if (error.status === 401)
                {
                    AuthStatusService.logger.debug('Received 401 - user not authenticated (no 302 redirect)');
                    this.authStatusSubject.next({ isAuthenticated: false, reason: 'unauthorized' });
                } else
                {
                    AuthStatusService.logger.error('Auth status check network/server error');
                    this.authStatusSubject.next({ isAuthenticated: false, reason: 'error' });
                }

                return of(false);
            }),
            share()
        );
    }

    /**
     * Get user profile data using cached endpoint.
     * Server caches this data to minimize database queries.
     * 
     * Enhanced error handling for different response scenarios.
     * 
     * @returns Observable<boolean> - true if profile retrieved successfully
     */
    public getProfile(): Observable<boolean>
    {
        AuthStatusService.logger.debug('getProfile() - Using cached profile endpoint');

        const obs = this.authStatusDelegate.getProfile();

        return obs.pipe(
            mergeMap((response: IAuthProfileResponse) =>
            {
                if (response.success && response.userDetail)
                {
                    AuthStatusService.logger.debug('Profile retrieved successfully from cache');
                    // Update the authentication service with the profile data
                    this.authenticationService.updateSession({ data: response });
                    return of(true);
                }

                AuthStatusService.logger.warn('Profile response indicates failure or missing data');
                return of(false);
            }),
            catchError((error) =>
            {
                AuthStatusService.logger.error(`getProfile failed: ${JSON.stringify(error)}`);

                if (error.status === 401)
                {
                    AuthStatusService.logger.debug('Profile fetch received 401 - user not authenticated');
                } else
                {
                    AuthStatusService.logger.error('Profile fetch network/server error');
                }

                return of(false);
            }),
            share()
        );
    }

    /**
     * Quick token validation without database queries.
     * Use this for ultra-lightweight auth checks.
     * 
     * @returns Observable<boolean> - true if token is valid
     */
    public validateToken(): Observable<boolean>
    {
        AuthStatusService.logger.debug('validateToken() - Ultra-lightweight token validation');

        return this.authStatusDelegate.validate().pipe(
            map((response: IAuthValidateResponse) =>
            {
                AuthStatusService.logger.debug(`Token validation result: ${response.isValid}`);
                return response.isValid;
            }),
            catchError((error) =>
            {
                AuthStatusService.logger.error(`Token validation failed: ${JSON.stringify(error)}`);
                return of(false);
            }),
            share()
        );
    }

    /**
     * Get the current auth status (synchronous).
     */
    public get currentStatus(): IAuthStatusResponse | null
    {
        return this.authStatusSubject.value;
    }

    /**
     * Check if currently authenticated based on last status check.
     */
    public get isAuthenticated(): boolean
    {
        const status = this.authStatusSubject.value;
        return status && status.isAuthenticated === true;
    }

    /**
     * Force a logout by clearing the auth status and stopping periodic checks.
     * This should be called when authentication fails or user manually logs out.
     */
    public clearAuthStatus(): void
    {
        AuthStatusService.logger.debug('Clearing auth status');
        this.authStatusSubject.next({ isAuthenticated: false, reason: 'logged_out' });
        this.stopPeriodicChecks();
    }
}
